import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const PlayerContext = createContext();

export function usePlayer() {
  return useContext(PlayerContext);
}

export function PlayerProvider({ children }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [isPlayerVisible, setIsPlayerVisible] = useState(true);
  const audioRef = useRef(null);
  const youtubeRef = useRef(null);

  // Efeito para garantir que o player comece pausado ao fazer login
  useEffect(() => {
    handleStop();
    setCurrentTrack(null);
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
      audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioRef.current.addEventListener('ended', handleEnded);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        audioRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audioRef.current.removeEventListener('ended', handleEnded);
      }
    };
  }, [currentTrack]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const handlePlayVideo = (video) => {
    setError(null); // Reseta o erro ao tentar tocar um novo áudio
    
    // Se for um vídeo do YouTube
    if (video.videoId) {
      if (currentTrack?.id === video.id) {
        if (isPlaying) {
          youtubeRef.current?.pauseVideo();
          setIsPlaying(false);
        } else {
          youtubeRef.current?.playVideo();
          setIsPlaying(true);
        }
        return;
      }

      // Carregar o player do YouTube se ainda não estiver carregado
      if (!window.YT) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        window.onYouTubeIframeAPIReady = () => {
          createYouTubePlayer(video);
        };
      } else {
        createYouTubePlayer(video);
      }
    } else {
      // Se for um arquivo de áudio normal
      if (currentTrack?.id === video.id) {
        if (isPlaying) {
          audioRef.current?.pause();
          setIsPlaying(false);
        } else {
          audioRef.current?.play()
            .then(() => {
              setIsPlaying(true);
            })
            .catch(error => {
              console.error('Error playing audio:', error);
              setIsPlaying(false);
              setError('Esta meditação ainda não está disponível');
            });
        }
        return;
      }

      setCurrentTrack(video);
      if (audioRef.current) {
        audioRef.current.src = video.audioUrl;
        audioRef.current.load();
        if (video.autoplay !== false) {
          audioRef.current.play()
            .then(() => {
              setIsPlaying(true);
            })
            .catch(error => {
              console.error('Error playing audio:', error);
              setIsPlaying(false);
              setError('Esta meditação ainda não está disponível');
            });
        } else {
          setIsPlaying(false);
        }
      } else {
        const audio = new Audio(video.audioUrl);
        audio.volume = volume;
        audioRef.current = audio;
        if (video.autoplay !== false) {
          audio.play()
            .then(() => {
              setIsPlaying(true);
            })
            .catch(error => {
              console.error('Error playing audio:', error);
              setIsPlaying(false);
              setError('Esta meditação ainda não está disponível');
            });
        } else {
          setIsPlaying(false);
        }
      }
    }
  };

  const createYouTubePlayer = (video) => {
    // Limpar player anterior se existir
    if (youtubeRef.current) {
      youtubeRef.current.destroy();
    }

    // Criar elemento para o player
    const playerDiv = document.createElement('div');
    playerDiv.id = 'youtube-player';
    playerDiv.style.display = 'none';
    document.body.appendChild(playerDiv);

    // Criar novo player
    youtubeRef.current = new window.YT.Player('youtube-player', {
      height: '0',
      width: '0',
      videoId: video.videoId,
      playerVars: {
        autoplay: 1,
        controls: 0,
      },
      events: {
        onReady: (event) => {
          event.target.setVolume(volume * 100);
          event.target.playVideo();
          setIsPlaying(true);
          setCurrentTrack(video);
        },
        onStateChange: (event) => {
          if (event.data === window.YT.PlayerState.ENDED) {
            handleEnded();
          }
          if (event.data === window.YT.PlayerState.PLAYING) {
            setDuration(event.target.getDuration());
          }
        }
      }
    });
  };

  const handlePause = () => {
    if (currentTrack?.videoId) {
      youtubeRef.current?.pauseVideo();
    } else {
      audioRef.current?.pause();
    }
    setIsPlaying(false);
  };

  const handlePlay = () => {
    if (currentTrack?.videoId) {
      youtubeRef.current?.playVideo();
      setIsPlaying(true);
    } else if (currentTrack && audioRef.current) {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(error => {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
      });
    }
  };

  const handleStop = () => {
    if (currentTrack?.videoId) {
      youtubeRef.current?.stopVideo();
    } else if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setProgress(0);
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (currentTrack?.videoId) {
      youtubeRef.current?.setVolume(newVolume * 100);
    } else if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleProgressChange = (event, newValue) => {
    if (currentTrack?.videoId) {
      const newTime = (newValue / 100) * duration;
      youtubeRef.current?.seekTo(newTime, true);
      setProgress(newTime);
    } else if (audioRef.current) {
      audioRef.current.currentTime = newValue;
      setProgress(newValue);
    }
  };

  // Atualizar progresso para vídeos do YouTube
  useEffect(() => {
    let progressInterval;
    if (isPlaying && currentTrack?.videoId) {
      progressInterval = setInterval(() => {
        if (youtubeRef.current?.getCurrentTime) {
          const currentTime = youtubeRef.current.getCurrentTime();
          setProgress(currentTime);
        }
      }, 1000);
    }
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [isPlaying, currentTrack]);

  const togglePlayerVisibility = () => {
    setIsPlayerVisible(prev => !prev);
  };

  const value = {
    isPlaying,
    currentTrack,
    volume,
    progress,
    duration,
    error,
    isPlayerVisible,
    handlePlayVideo,
    handlePause,
    handlePlay,
    handleStop,
    handleVolumeChange,
    handleProgressChange,
    togglePlayerVisibility
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
} 